import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, Check } from 'lucide-react';
import { ONLY_SHOW_OPTIONS, ONLY_SHOW_LOOKUP } from './leadsConstants.jsx';

export default function OnlyShowDropdown({
  value, onChange,
  days, setDays, zip, setZip, year, setYear, month, setMonth,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [popPos, setPopPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => { if (!open) setQ(''); }, [open]);

  const filteredGroups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return ONLY_SHOW_OPTIONS;
    return ONLY_SHOW_OPTIONS
      .map((g) => ({ ...g, items: g.items.filter((it) => it.label.toLowerCase().includes(needle)) }))
      .filter((g) => g.items.length > 0);
  }, [q]);

  useLayoutEffect(() => {
    if (!open) return;
    function place() {
      const btn = btnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      setPopPos({
        top: r.bottom + 4,
        left: r.left,
        width: r.width,
      });
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    const onClick = (e) => {
      const inAnchor = ref.current && ref.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inAnchor && !inPop) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const current = value ? ONLY_SHOW_LOOKUP[value] : null;
  const companion = current?.companion;

  return (
    <div ref={ref} className="relative">
      <label className="block text-[11px] text-white/60 mb-1">Only show</label>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input flex items-center justify-between text-left w-full"
      >
        <span className={current ? 'text-white' : 'text-white/40'}>
          {current ? current.label : 'Choose a filter…'}
        </span>
        <ChevronDown className="w-4 h-4 text-white/40" />
      </button>

      {companion === 'days' && (
        <input
          type="number" min="1" max="365" value={days}
          onChange={(e) => setDays(e.target.value)}
          placeholder="# days"
          className="input mt-2 w-32"
        />
      )}
      {companion === 'zip' && (
        <input
          type="text" maxLength="5" value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
          placeholder="ZIP code"
          className="input mt-2 w-32"
        />
      )}
      {companion === 'year' && (
        <input
          type="number" min="1900" max="2100" value={year}
          onChange={(e) => setYear(e.target.value)}
          placeholder="e.g. 1970"
          className="input mt-2 w-32"
        />
      )}
      {companion === 'month' && (
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="input mt-2 w-40"
        >
          <option value="">Pick month…</option>
          {['January','February','March','April','May','June','July','August','September','October','November','December']
            .map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
      )}

      {open && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed',
            top: popPos.top,
            left: popPos.left,
            width: popPos.width,
            zIndex: 9999,
          }}
          className="bg-civix-panel border border-civix-border rounded-2xl shadow-panel max-h-96 overflow-y-auto p-2"
        >
          <div className="sticky top-0 bg-civix-panel z-10">
            <div className="flex items-center justify-between px-2 py-1 mb-1">
              <span className="text-[10px] uppercase tracking-wide text-white/40">
                {Object.keys(ONLY_SHOW_LOOKUP).length} filters
              </span>
              {value && (
                <button
                  onClick={() => { onChange(''); setOpen(false); }}
                  className="text-[11px] text-civix-purpleSoft hover:text-white"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="px-2 pb-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search filters…"
                  className="w-full bg-white/10 rounded-sm px-7 py-1.5 text-xs text-white placeholder-white/40 outline-hidden focus:ring-1 focus:ring-civix-purple/50"
                />
              </div>
            </div>
          </div>
          {filteredGroups.length === 0 && (
            <div className="px-3 py-3 text-xs text-white/40 text-center">No matches</div>
          )}
          {filteredGroups.map((g) => (
            <div key={g.group} className="mb-2">
              <div className="text-[10px] uppercase tracking-wide text-white/40 px-2 py-1">
                {g.group}
              </div>
              {g.items.map((it) => (
                <button
                  key={it.value}
                  onClick={() => { onChange(it.value); setOpen(false); }}
                  className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-white/5 flex items-center justify-between ${
                    value === it.value ? 'bg-civix-purple/20 text-white' : 'text-white/80'
                  }`}
                >
                  <span>{it.label}</span>
                  {value === it.value && <Check className="w-4 h-4 text-civix-cyan" />}
                </button>
              ))}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
